import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { UserCheck, UserX, Clock, DollarSign, Users, Link2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminAffiliate() {
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const [affRes, statsRes] = await Promise.all([
        apiClient.get('/admin/affiliates', { params }),
        apiClient.get('/admin/affiliates/stats')
      ]);
      setAffiliates(affRes.data.affiliates);
      setTotal(affRes.data.total);
      setStats(statsRes.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    try { await apiClient.put(`/admin/affiliates/${id}/status`, { status }); fetchData(); }
    catch (err) { /* silent */ }
  };

  const updateCommission = async (id, rate) => {
    try { await apiClient.put(`/admin/affiliates/${id}/commission`, { commission_rate: parseFloat(rate) }); fetchData(); }
    catch (err) { /* silent */ }
  };

  const statCards = stats ? [
    { label: "Total Affiliates", value: stats.total_affiliates, icon: Users, color: "bg-blue-500/10 text-blue-400" },
    { label: "Approved", value: stats.approved, icon: UserCheck, color: "bg-emerald-500/10 text-emerald-400" },
    { label: "Pending", value: stats.pending, icon: Clock, color: "bg-amber-500/10 text-amber-400" },
    { label: "Total Referrals", value: stats.total_referrals, icon: Link2, color: "bg-violet-500/10 text-violet-400" },
    { label: "Total Earnings", value: `CA$${stats.total_earnings.toFixed(2)}`, icon: TrendingUp, color: "bg-cyan-500/10 text-cyan-400" },
    { label: "Total Paid", value: `CA$${stats.total_paid.toFixed(2)}`, icon: DollarSign, color: "bg-green-500/10 text-green-400" },
  ] : [];

  const statusFilters = ["", "pending", "approved", "rejected", "suspended"];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-affiliate">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1">Affiliate Program</h1>
          <p className="text-white/40 text-sm">SMAHOO ME.WELL partner management</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
              {statCards.map(s => (
                <div key={s.label} className="bg-[#161821] border border-white/5 rounded-2xl p-4">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${s.color}`}><s.icon className="w-4 h-4" /></div>
                  <p className="text-xl font-semibold text-white">{s.value}</p>
                  <p className="text-white/40 text-xs">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-6">
              {statusFilters.map(f => (
                <button key={f || "all"} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-xl text-xs transition-all ${filter === f ? "bg-amber-500/10 text-amber-400" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`} data-testid={`filter-${f || "all"}`}>
                  {f || "All"}
                </button>
              ))}
            </div>

            <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-white/30 text-xs uppercase border-b border-white/5">
                    <th className="text-left p-4">Partner</th><th className="text-left p-4">Referral Code</th><th className="text-left p-4">Commission</th><th className="text-left p-4">Referrals</th><th className="text-left p-4">Earnings</th><th className="text-left p-4">Status</th><th className="text-left p-4">Actions</th>
                  </tr></thead>
                  <tbody>
                    {affiliates.length > 0 ? affiliates.map(a => (
                      <tr key={a.affiliate_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`aff-${a.affiliate_id}`}>
                        <td className="p-4"><div className="text-white/80">{a.name}</div><div className="text-white/30 text-xs">{a.email}</div></td>
                        <td className="p-4"><code className="text-amber-400/80 text-xs bg-amber-500/5 px-2 py-1 rounded">{a.referral_code}</code></td>
                        <td className="p-4">
                          <input type="number" value={a.commission_rate} onChange={(e) => updateCommission(a.affiliate_id, e.target.value)} className="w-16 bg-transparent border border-white/10 rounded px-2 py-1 text-white text-xs text-center" min="0" max="100" step="0.5" />
                          <span className="text-white/30 text-xs ml-1">%</span>
                        </td>
                        <td className="p-4 text-white/50">{a.total_referrals}</td>
                        <td className="p-4 text-emerald-400">CA${a.total_earnings.toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            a.status === "approved" ? "bg-emerald-500/10 text-emerald-400" :
                            a.status === "pending" ? "bg-amber-500/10 text-amber-400" :
                            a.status === "rejected" ? "bg-red-500/10 text-red-400" :
                            "bg-white/5 text-white/40"
                          }`}>{a.status}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {a.status !== "approved" && (
                              <button onClick={() => updateStatus(a.affiliate_id, "approved")} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-white/30 hover:text-emerald-400" title="Approve"><UserCheck className="w-4 h-4" /></button>
                            )}
                            {a.status !== "suspended" && a.status !== "rejected" && (
                              <button onClick={() => updateStatus(a.affiliate_id, "suspended")} className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/30 hover:text-red-400" title="Suspend"><UserX className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7" className="p-8 text-center text-white/30">No affiliates found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
